<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  translationKey: {
    type: String,
    required: true,
  },
  entity: {
    type: Object,
    required: true,
  },
  deletePayload: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['deleteSuccess']);

const { t } = useI18n();
const store = useStore();
const deleteDialogRef = ref(null);
const isOpen = ref(false);
const i18nKey = computed(() => {
  return props.translationKey || props.type.toUpperCase();
});

// 类型到 store 模块名的映射（处理单复数）
const storeModuleName = computed(() => {
  const typeMap = {
    Assistant: 'Assistants',
    KnowledgeBase: 'KnowledgeBases',
    Faq: 'Faqs',
    Document: 'Documents',
    CustomTool: 'CustomTools',
    PendingFaq: 'PendingFaqs',
  };
  return typeMap[props.type] || props.type;
});

const deleteEntity = async payload => {
  if (!payload) return;

  try {
    await store.dispatch(`pilot${storeModuleName.value}/delete`, payload);
    emit('deleteSuccess');
    useAlert(t(`PILOT.${i18nKey.value}.DELETE.SUCCESS_MESSAGE`));
  } catch (error) {
    useAlert(t(`PILOT.${i18nKey.value}.DELETE.ERROR_MESSAGE`));
  }
};

const handleDialogConfirm = async () => {
  if (!props.entity) return;

  const finalPayload = props.deletePayload || props.entity.id;
  await deleteEntity(finalPayload);
  isOpen.value = false;
  deleteDialogRef.value?.close();
};

const open = () => {
  isOpen.value = true;
  deleteDialogRef.value?.open();
};

const close = () => {
  isOpen.value = false;
  deleteDialogRef.value?.close();
};

defineExpose({ dialogRef: deleteDialogRef, open, close });
</script>

<template>
  <Dialog
    v-if="entity"
    ref="deleteDialogRef"
    type="alert"
    :title="t(`PILOT.${i18nKey}.DELETE.TITLE`)"
    :description="t(`PILOT.${i18nKey}.DELETE.DESCRIPTION`)"
    :confirm-button-label="t(`PILOT.${i18nKey}.DELETE.CONFIRM`)"
    @confirm="handleDialogConfirm"
  />
</template>
